// ─── src/api/doorCodes.ts ────────────────────────────────────────────────────
//
// Door codes unlock the scanner at scan.startupsyria.org for receptionists.
//   • admin (x-api-key) — any event, plus "all" staff codes
//   • catalyst (JWT)    — their own meetups only

import { apiClient } from "./registration";
import { catalystClient } from "./catalystPortal";

export type DoorScope = "gew_event" | "meetup" | "all";

export interface DoorCode {
  id: string;
  /** XXXX-XXXX */
  code: string;
  scope: DoorScope;
  eventId: string | null;
  /** Admin listing only. */
  eventTitle?: string | null;
  label: string | null;
  createdBy: string;
  expiresAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  active: boolean;
  /** scan.startupsyria.org/#XXXX-XXXX — opens the scanner with the code filled in. */
  link: string;
}

export const adminDoorCodesApi = {
  list: (scope?: DoorScope, eventId?: string) =>
    apiClient
      .get<DoorCode[]>("/door-codes", { params: { ...(scope && { scope }), ...(eventId && { eventId }) } })
      .then((r) => r.data),
  createForEvent: (scope: "gew_event" | "meetup", eventId: string, label?: string) =>
    apiClient.post<DoorCode>("/door-codes", { scope, eventId, label }).then((r) => r.data),
  /** Staff code for every event; valid until the end of `validUntil` (YYYY-MM-DD). */
  createStaff: (label?: string, validUntil?: string) =>
    apiClient.post<DoorCode>("/door-codes", { scope: "all", label, validUntil }).then((r) => r.data),
  revoke: (id: string) => apiClient.delete<DoorCode>(`/door-codes/${id}`).then((r) => r.data),
};

export const catalystDoorCodesApi = {
  list: (eventId: string) =>
    catalystClient.get<DoorCode[]>(`/catalyst-portal/events/${eventId}/door-codes`).then((r) => r.data),
  create: (eventId: string, label?: string) =>
    catalystClient
      .post<DoorCode>(`/catalyst-portal/events/${eventId}/door-codes`, { label })
      .then((r) => r.data),
  revoke: (id: string) =>
    catalystClient.delete<DoorCode>(`/catalyst-portal/door-codes/${id}`).then((r) => r.data),
};

/** Ready-to-send text for the receptionist (WhatsApp / copy). */
export function doorCodeMessage(c: DoorCode): string {
  return [
    `رمز الباب: ${c.code}`,
    "افتح الرابط على هاتفك واكتب اسمك لبدء مسح تذاكر الحضور:",
    c.link,
    c.scope === "all" ? "رمز الفريق — يقبل تذاكر جميع الفعاليات." : "الرمز صالح حتى نهاية يوم الفعالية.",
  ].join("\n");
}

export function doorCodeError(err: unknown): string {
  const msg = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join(", ");
  return typeof msg === "string" ? msg : "Something went wrong — try again.";
}
